﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreatePromotionRequest,
  Promotion,
  PromotionRedemption,
  UpdatePromotionRequest,
} from "@ticketnjoy/types";

import { apiClient } from "@/lib/api-client";

export const promotionsQueryKey = ["promotions"] as const;
export const promotionQueryKey = (id: string) =>
  ["promotions", "detail", id] as const;
export const promotionRedemptionsQueryKey = (id: string) =>
  ["promotions", "redemptions", id] as const;

export function usePromotionsQuery() {
  return useQuery<Promotion[]>({
    queryKey: promotionsQueryKey,
    queryFn: () => apiClient<Promotion[]>("/promotions"),
  });
}

export function usePromotionQuery(id: string) {
  return useQuery<Promotion>({
    queryKey: promotionQueryKey(id),
    queryFn: () => apiClient<Promotion>(`/promotions/${id}`),
    enabled: Boolean(id),
  });
}

export function usePromotionRedemptionsQuery(id: string) {
  return useQuery<PromotionRedemption[]>({
    queryKey: promotionRedemptionsQueryKey(id),
    queryFn: () => apiClient<PromotionRedemption[]>(`/promotions/${id}/redemptions`),
    enabled: Boolean(id),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ["promotions"] });
  if (id) qc.invalidateQueries({ queryKey: promotionQueryKey(id) });
}

export function useCreatePromotionMutation() {
  const qc = useQueryClient();
  return useMutation<Promotion, Error, CreatePromotionRequest>({
    mutationFn: (body) =>
      apiClient<Promotion>("/promotions", { method: "POST", body }),
    onSuccess: (p) => {
      qc.setQueryData(promotionQueryKey(p.id), p);
      invalidate(qc);
    },
  });
}

export function useUpdatePromotionMutation(id: string) {
  const qc = useQueryClient();
  return useMutation<Promotion, Error, UpdatePromotionRequest>({
    mutationFn: (body) =>
      apiClient<Promotion>(`/promotions/${id}`, { method: "PUT", body }),
    onSuccess: (p) => {
      qc.setQueryData(promotionQueryKey(p.id), p);
      invalidate(qc, id);
    },
  });
}

export function useDeletePromotionMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient<void>(`/promotions/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidate(qc),
  });
}
